import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import StarRating from "../components/StarRating";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import Marquee from "react-fast-marquee";
import {
  Wrench, Lightning, Hammer, PaintRoller, GridFour, PaintBucket, House, Buildings,
  Toolbox, Tree, Broom, Key, Flame, ForkKnife, Bathtub, Stack, Door, Truck, Bug, Monitor,
  ShieldCheck, MapPin, ChatCircle, CheckCircle, ArrowRight, CurrencyGbp, Sparkle, Clock,
} from "@phosphor-icons/react";

const ICONS = {
  Wrench, Lightning, Hammer, PaintRoller, Squares: GridFour, PaintBucket, House, Buildings, Toolbox, Tree,
  SprayBottle: Broom, Key, Flame, ForkKnife, Bathtub, Stack, Door, Truck, Bug, Television: Monitor,
};

const HERO_IMG =
  "https://images.unsplash.com/photo-1589939705384-5185137a7f0f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxjYXJwZW50ZXIlMjB3b3JraW5nJTIwd29vZHxlbnwwfHx8fDE3ODA2MzE2NzJ8MA&ixlib=rb-4.1.0&q=85";
const ELEC_IMG =
  "https://images.pexels.com/photos/33694016/pexels-photo-33694016.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const PLUMBER_IMG =
  "https://images.unsplash.com/photo-1659353591742-9fa64d94738e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHw0fHxmcmllbmRseSUyMHBsdW1iZXIlMjBwb3J0cmFpdHxlbnwwfHx8fDE3ODA2MzE2NzJ8MA&ixlib=rb-4.1.0&q=85";
const TESTIMONIAL_1 =
  "https://images.unsplash.com/photo-1677901766272-8c9d7b49f07c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxzbWlsaW5nJTIwZGl2ZXJzZSUyMHBlb3BsZSUyMHBvcnRyYWl0fGVufDB8fHx8MTc4MDYzMTY3Mnww&ixlib=rb-4.1.0&q=85";
const TESTIMONIAL_2 =
  "https://images.pexels.com/photos/4625044/pexels-photo-4625044.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Landing() {
  const [trades, setTrades] = useState([]);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/meta/trades");
        setTrades(data || []);
      } catch {}
    })();
  }, []);

  const goPostJob = () => navigate(user ? "/post-job" : "/register?role=customer");

  return (
    <Layout>
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-12 lg:pt-20 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[rgba(17,82,59,0.08)] text-[var(--brand-green)] text-xs font-semibold tracking-wide" data-testid="hero-eyebrow">
              <Sparkle size={14} weight="fill" /> Made for the UK · myhandyhub.co.uk
            </span>
            <h1 className="font-display font-extrabold mt-5 text-4xl sm:text-5xl lg:text-6xl tracking-tight" style={{ color: "var(--brand-green)" }}>
              Your personal hub for <span style={{ color: "var(--brand-orange)" }}>local professionals</span>.
            </h1>
            <p className="mt-5 text-lg text-[var(--text-muted)] max-w-xl leading-relaxed">
              Post any home job in seconds and get matched with vetted UK tradespeople near you.
              Free for life for customers — just <strong style={{ color: "var(--brand-green)" }}>£3.99/month</strong> for trades.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <button onClick={goPostJob} className="btn-accent text-base" data-testid="hero-post-job-btn">
                Post a job — it's free <ArrowRight size={18} weight="bold" />
              </button>
              <Link to="/register?role=tradesperson" className="btn-primary text-base" data-testid="hero-join-trader-btn">
                I'm a tradesperson <Wrench size={18} weight="duotone" />
              </Link>
            </div>
            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm text-[var(--text-muted)]">
              <li className="flex items-center gap-2"><CheckCircle weight="fill" className="text-[var(--brand-orange)]" /> 100% free for customers</li>
              <li className="flex items-center gap-2"><CheckCircle weight="fill" className="text-[var(--brand-orange)]" /> Instant job alerts</li>
              <li className="flex items-center gap-2"><CheckCircle weight="fill" className="text-[var(--brand-orange)]" /> Built-in messenger</li>
            </ul>
          </div>
          <div className="lg:col-span-5">
            <div className="relative rounded-2xl overflow-hidden border border-[var(--border-stone)] shadow-[0_20px_60px_rgba(17,82,59,0.18)]">
              <img src={HERO_IMG} alt="UK carpenter at work" className="w-full h-[460px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-tr from-black/45 via-transparent to-transparent" />
              <div className="absolute left-4 bottom-4 right-4 bg-white/90 backdrop-blur rounded-xl p-4 flex items-center gap-3">
                <img src={PLUMBER_IMG} alt="Tom" className="w-12 h-12 rounded-full object-cover border border-[var(--border-stone)]" />
                <div className="flex-1">
                  <div className="font-display font-semibold text-[var(--brand-green)]">Tom's Plumbing Co.</div>
                  <StarRating value={4.9} count={47} />
                </div>
                <span className="px-2 py-1 rounded-md text-xs font-semibold bg-[rgba(17,82,59,0.1)] text-[var(--brand-green)]">
                  <ShieldCheck size={12} weight="fill" className="inline -mt-0.5 mr-1" />Verified
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Editorial trade ribbon */}
        <div className="bg-[#EFEDE4] border-y border-[var(--border-stone)] py-3">
          <Marquee gradient={false} speed={28} pauseOnHover>
            {trades.map((t) => {
              const Icon = ICONS[t.icon] || Wrench;
              return (
                <span key={t.slug} className="inline-flex items-center gap-2 px-6 text-[var(--brand-green)] font-display font-semibold">
                  <Icon size={20} weight="duotone" /> {t.name}
                  <span className="text-[var(--text-muted)] mx-2">·</span>
                </span>
              );
            })}
          </Marquee>
        </div>
      </section>

      {/* HOW IT WORKS — Bento */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20" id="how-it-works" data-testid="how-section">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-12">
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl" style={{ color: "var(--brand-green)" }}>How MyHandyHub works</h2>
            <p className="mt-2 text-[var(--text-muted)] max-w-2xl">A simple two-sided platform built around fairness, speed, and trust.</p>
          </div>

          <div className="md:col-span-7 brand-card p-8 relative overflow-hidden">
            <span className="absolute -top-6 -right-6 text-[140px] font-display font-extrabold leading-none text-[rgba(17,82,59,0.05)]">01</span>
            <span className="inline-flex items-center gap-2 text-xs font-semibold text-[var(--brand-orange)] uppercase tracking-wider">For customers</span>
            <h3 className="font-display font-bold text-2xl mt-2" style={{ color: "var(--brand-green)" }}>Post your job for free</h3>
            <p className="mt-2 text-[var(--text-muted)]">Pick a trade, describe your job, add your postcode and optional photos. We instantly alert matching trades in your area.</p>
            <ul className="mt-4 grid grid-cols-2 gap-2 text-sm text-[var(--text-muted)]">
              <li className="flex items-center gap-2"><MapPin weight="fill" className="text-[var(--brand-orange)]" /> UK postcode matching</li>
              <li className="flex items-center gap-2"><Clock weight="fill" className="text-[var(--brand-orange)]" /> Instant alerts</li>
              <li className="flex items-center gap-2"><ChatCircle weight="fill" className="text-[var(--brand-orange)]" /> Private messenger</li>
              <li className="flex items-center gap-2"><CurrencyGbp weight="bold" className="text-[var(--brand-orange)]" /> Always free</li>
            </ul>
          </div>

          <div className="md:col-span-5 brand-card p-8 noise-bg" style={{ background: "var(--brand-green)", color: "#fff" }}>
            <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#FFD8B8" }}>For trades</span>
            <h3 className="font-display font-bold text-2xl mt-2">Just £3.99 a month</h3>
            <p className="mt-2 text-white/80">Unlimited job alerts. No success fees. No per-lead charges. Adjust your work radius from 0–50 miles, change anytime.</p>
            <Link to="/register?role=tradesperson" className="mt-5 btn-accent" data-testid="how-join-trader">
              Join the trade network <ArrowRight size={16} weight="bold" />
            </Link>
          </div>

          <div className="md:col-span-4 brand-card p-6">
            <Wrench size={28} weight="duotone" className="text-[var(--brand-orange)]" />
            <h4 className="font-display font-semibold text-lg mt-3" style={{ color: "var(--brand-green)" }}>Smart matching</h4>
            <p className="text-sm text-[var(--text-muted)] mt-1">Trades pick exactly which jobs they want. No spam, no irrelevant leads.</p>
          </div>
          <div className="md:col-span-4 brand-card p-6">
            <ChatCircle size={28} weight="duotone" className="text-[var(--brand-orange)]" />
            <h4 className="font-display font-semibold text-lg mt-3" style={{ color: "var(--brand-green)" }}>Private chat</h4>
            <p className="text-sm text-[var(--text-muted)] mt-1">Discuss quotes inside the platform — share contact info only when ready.</p>
          </div>
          <div className="md:col-span-4 brand-card p-6">
            <ShieldCheck size={28} weight="duotone" className="text-[var(--brand-orange)]" />
            <h4 className="font-display font-semibold text-lg mt-3" style={{ color: "var(--brand-green)" }}>Verified badges</h4>
            <p className="text-sm text-[var(--text-muted)] mt-1">ID & insurance checks for serious professionals. Reviews from real, completed jobs.</p>
          </div>
        </div>
      </section>

      {/* TRADE CATEGORIES */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-20" data-testid="trades-section">
        <div className="flex items-end justify-between gap-4 mb-8">
          <div>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl" style={{ color: "var(--brand-green)" }}>Every trade you'd want at home</h2>
            <p className="mt-2 text-[var(--text-muted)] max-w-2xl">From a leaky tap to a full extension, MyHandyHub covers it all.</p>
          </div>
          <Link to="/find-trades" className="btn-ghost hidden sm:inline-flex" data-testid="trades-see-all">See all <ArrowRight size={16} /></Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {trades.map((t) => {
            const Icon = ICONS[t.icon] || Wrench;
            return (
              <Link
                key={t.slug}
                to={`/find-trades?trade=${t.slug}`}
                className="brand-card p-4 flex flex-col items-start gap-2 group"
                data-testid={`trade-card-${t.slug}`}
              >
                <span className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: "rgba(226,109,41,0.1)" }}>
                  <Icon size={22} weight="duotone" className="text-[var(--brand-orange)]" />
                </span>
                <span className="font-display font-semibold text-sm text-[var(--brand-green)]">{t.name}</span>
                <span className="text-xs text-[var(--text-muted)] group-hover:text-[var(--brand-green)] transition-colors">Browse trades →</span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* PRICING STRIP */}
      <section className="bg-[#EFEDE4] border-y border-[var(--border-stone)] py-16" data-testid="pricing-strip">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl" style={{ color: "var(--brand-green)" }}>Fair pricing. No surprises.</h2>
            <p className="mt-3 text-[var(--text-muted)] max-w-xl">Customers post jobs free forever. Trades pay one flat £3.99/month — no per-lead fees, no success fees.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/pricing" className="btn-primary" data-testid="strip-pricing-btn">See pricing</Link>
              <Link to="/register?role=tradesperson" className="btn-accent" data-testid="strip-join-trader">Start as a trader</Link>
            </div>
            <p className="text-xs text-[var(--text-muted)] mt-4">ℹ️ Standard payment processing fees are non-refundable.</p>
          </div>
          <div className="brand-card p-8 bg-white">
            <div className="flex items-baseline gap-2">
              <span className="font-display font-extrabold text-5xl" style={{ color: "var(--brand-green)" }}>£3.99</span>
              <span className="text-[var(--text-muted)]">/ month</span>
            </div>
            <p className="text-sm text-[var(--text-muted)] mt-2">Unlimited job alerts · Range 0–50 mi · Quotes & messaging</p>
            <ul className="mt-4 space-y-2 text-sm">
              <li className="flex items-center gap-2"><CheckCircle weight="fill" className="text-[var(--brand-orange)]" /> Unlimited matching alerts</li>
              <li className="flex items-center gap-2"><CheckCircle weight="fill" className="text-[var(--brand-orange)]" /> Adjustable work radius slider</li>
              <li className="flex items-center gap-2"><CheckCircle weight="fill" className="text-[var(--brand-orange)]" /> Earnings & job history dashboard</li>
              <li className="flex items-center gap-2"><CheckCircle weight="fill" className="text-[var(--brand-orange)]" /> No commissions, no success fees</li>
            </ul>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20" data-testid="testimonials-section">
        <h2 className="font-display font-extrabold text-3xl sm:text-4xl" style={{ color: "var(--brand-green)" }}>Trusted by people across the UK</h2>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <article className="brand-card p-7 flex gap-5 items-start">
            <img src={TESTIMONIAL_1} alt="Customer" className="w-16 h-16 rounded-full object-cover" />
            <div>
              <StarRating value={5} />
              <p className="mt-2 text-[var(--text-main)]">"Posted a leak job at breakfast. Had two quotes by lunch and a plumber on-site by 4pm. Honestly brilliant."</p>
              <p className="mt-2 text-sm text-[var(--text-muted)]">Albert · Huddersfield (HD2)</p>
            </div>
          </article>
          <article className="brand-card p-7 flex gap-5 items-start">
            <img src={TESTIMONIAL_2} alt="Customer" className="w-16 h-16 rounded-full object-cover" />
            <div>
              <StarRating value={5} />
              <p className="mt-2 text-[var(--text-main)]">"As an electrician, £3.99 a month is unreal. I used to spend that in an hour on the old lead-gen sites."</p>
              <p className="mt-2 text-sm text-[var(--text-muted)]">Priya · Manchester (M14)</p>
            </div>
          </article>
        </div>
        <div className="mt-10 rounded-2xl overflow-hidden border border-[var(--border-stone)] relative">
          <img src={ELEC_IMG} alt="Electrician working" className="w-full h-72 object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/65 to-transparent flex items-center">
            <div className="px-8 max-w-xl text-white">
              <h3 className="font-display font-extrabold text-3xl">Ready to find your trade?</h3>
              <p className="mt-2 text-white/85">Post your first job in under 60 seconds. No card, no commitment.</p>
              <button onClick={goPostJob} className="btn-accent mt-4" data-testid="cta-post-job-bottom">Post a job now</button>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
